import type { BillingStrategy } from "./BillingStrategy"
import { DefaultBillingStrategy } from "./DefaultBillingStrategy"
import { DivanaBillingStrategy } from "./DivanaBillingStrategy"

export type { BillingStrategy, BillingOpts } from "./BillingStrategy"

/**
 * Registry key normaliser. Applied to BOTH sides — the strategy's own `key` when
 * it is registered, and the setting value when it is looked up — so the two can
 * never disagree about capitalisation.
 *
 * This matters: registering with `key: "Divana"` while looking up a lowercased
 * setting value silently missed the map and fell through to the default
 * pipeline, i.e. the shop's custom billing quietly stopped applying with no
 * error anywhere. Normalising only at lookup is not enough.
 */
const normaliseKey = (key?: string | null) => (key || "").trim().toLowerCase()

// Per-shop billing strategies, keyed by the `BillingPattern` setting value.
// Add a new customer's rule by dropping in a strategy file and listing it here;
// its `key` may be written in any capitalisation.
const STRATEGIES: Record<string, BillingStrategy> = Object.fromEntries(
    [DefaultBillingStrategy, DivanaBillingStrategy].map((s) => [normaliseKey(s.key), s])
)

/**
 * Resolve a strategy by key. Unknown / empty keys fall back to the default
 * pipeline, so a mis-seeded setting can never break checkout.
 *
 * Pure lookup — prefer `getActiveBillingStrategy()` at call sites. This stays
 * exported for the startup wiring and for tests that exercise one strategy
 * directly without touching module state.
 */
export function getBillingStrategy(key?: string | null): BillingStrategy {
    return STRATEGIES[normaliseKey(key)] || DefaultBillingStrategy
}

/**
 * The shop's billing strategy, resolved ONCE at startup.
 *
 * This is deliberately module-level state rather than a field on
 * tableBillingGlobal: the strategy is a property of the SHOP, not of a bill. It
 * is read from the `BillingStrategy` setting when settings finish loading and
 * never changes for the life of the page — unlike sc / vat / roundingMode, which
 * a cashier can override per table mid-shift. Threading a constant through every
 * calculateBilling call just to re-resolve it on each pass was noise.
 *
 * Defaults to the standard pipeline, so any code path that prices a bill before
 * settings land (or in a unit test that never calls the setter) behaves exactly
 * as it did before strategies existed.
 */
let activeStrategy: BillingStrategy = DefaultBillingStrategy

/**
 * Startup wiring. Called once from the settings store when settings are applied
 * (see settings.computeInit). Safe to call again on a settings refetch — it just
 * re-resolves the same key.
 */
export function setActiveBillingStrategy(key?: string | null): BillingStrategy {
    activeStrategy = getBillingStrategy(key)
    return activeStrategy
}

/** The strategy every billing call site should use. */
export function getActiveBillingStrategy(): BillingStrategy {
    return activeStrategy
}

/** Test helper — restores the default so specs cannot leak state into each other. */
export function resetActiveBillingStrategy(): void {
    activeStrategy = DefaultBillingStrategy
}
